import {
	Button,
	Grid,
	Icon,
	type JVSIcons,
	Modal,
	Segment,
	type SemanticWIDTHS,
} from '@mairistem/composants';
import _ from 'lodash';
import React, { useState } from 'react';
import IconBlockNotePresentation from '../../../../constants/simulation/iconBlockNotePresentation';
import type LigneNotePresentation from '../../../../interfaces/simulation/ligneNotePresentation';
import BlockList from './BlockList';
import './Block.css';

const typeBlocks = [
	{
		type: 'texte rédigé',
		icon: IconBlockNotePresentation.TEXTE,
		blocks: [{ codeBlock: 'Texte', label: 'Texte' }],
	},
	{
		type: 'graphique',
		icon: IconBlockNotePresentation.GRAPHIQUE,
		blocks: [{ codeBlock: 'Graphique', label: 'Graphique' }],
	},
	{
		type: 'tableau',
		icon: IconBlockNotePresentation.TABLEAU,
		blocks: [{ codeBlock: 'Tableau', label: 'Tableau' }],
	},
	{
		type: 'ratio',
		icon: IconBlockNotePresentation.RATIO,
		blocks: [{ codeBlock: 'Ratio', label: 'Ratio' }],
	},
	{
		type: 'champ libre',
		icon: IconBlockNotePresentation.CHAMP_LIBRE,
		blocks: [
			{ codeBlock: 'Paragraphe', label: 'Paragraphe' },
			{ codeBlock: 'Titre', label: 'Titre' },
			{ codeBlock: 'Sous-titre', label: 'Sous-titre' },
		],
	},
];

interface BlockProps {
	onDelete: (identifiantLigne: number) => void;
	onAdd: (ligne: LigneNotePresentation, codeBlock: string) => void;
	onUpdate: (ligne: LigneNotePresentation) => void;
	ligne: LigneNotePresentation;
	isLastLigne: boolean;
}

const Block: React.FC<BlockProps> = ({
	onDelete,
	onUpdate,
	onAdd,
	ligne,
	isLastLigne,
}: BlockProps) => {
	const { identifiant: identifiantLigne, contenuCourt, codeBlock } = ligne;

	const [isHovered, setIsHovered] = useState(false);

	const [isDeleting, setIsDeleting] = useState(false);

	const [rowWidth, setRowWidth] = useState<SemanticWIDTHS>(16);

	const handleMouseEnter = () => {
		setRowWidth(15);
		setIsHovered(true);
	};
	const handleMouseLeave = () => {
		setIsHovered(false);
		setRowWidth(16);
	};

	const [typeBlock, setTypeBlock] = useState<JVSIcons>(
		IconBlockNotePresentation.AUTRE as JVSIcons,
	);

	const chooseIcon = (code: string) => {
		switch (code) {
			case 'Titre':
				setTypeBlock(IconBlockNotePresentation.CHAMP_LIBRE);
				break;
			case 'Sous-titre':
				setTypeBlock(IconBlockNotePresentation.CHAMP_LIBRE);
				break;
			case 'Paragraphe':
				setTypeBlock(IconBlockNotePresentation.CHAMP_LIBRE);
				break;
			case 'Graphique':
				setTypeBlock(IconBlockNotePresentation.GRAPHIQUE);
				break;
			case 'Ratio':
				setTypeBlock(IconBlockNotePresentation.RATIO);
				break;
			case 'Tableau':
				setTypeBlock(IconBlockNotePresentation.TABLEAU);
				break;
			case 'Comparatif':
				setTypeBlock(IconBlockNotePresentation.TABLEAU);
				break;
			case 'Texte':
				setTypeBlock(IconBlockNotePresentation.TEXTE);
				break;
			default:
				setTypeBlock(IconBlockNotePresentation.AUTRE);
				break;
		}
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: Pas besoin de 'chooseIcon'
	React.useEffect(() => {
		chooseIcon(codeBlock);
	}, [ligne, codeBlock]);

	const handleBlockSelect = (block) => {
		onAdd(ligne, block.codeBlock);
	};

	const handleStopPropagation = (e) => {
		e.stopPropagation();
	};

	const renderBlockList = () => (
		// biome-ignore lint/a11y/noStaticElementInteractions: Span
		<span
			className="blockListContainer"
			data-testid="blocksAddButton"
			onClick={handleStopPropagation}
			onKeyDown={handleStopPropagation}
			role="presentation"
		>
			<BlockList
				onSelect={handleBlockSelect}
				blocks={typeBlocks}
				popupTrigger={
					<Icon
						className="icon-action-add"
						data-testid="addBlockToNotePresentation"
						link
						iconSet="Lucide"
						name="Plus"
					/>
				}
			/>
		</span>
	);

	const handleDelete = () => {
		onDelete(identifiantLigne);
	};

	const handleUpdate = () => {
		onUpdate(ligne);
	};

	const handleDeletingSelection = () => {
		setIsDeleting(true);
	};

	const handleDeletingDeselection = () => {
		setIsDeleting(false);
	};

	return (
		<Grid
			fluid
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			columns={2}
			verticalAlign="middle"
		>
			<Grid.Column width={rowWidth}>
				<Segment
					className={`block-left-part ${isHovered ? 'block-left-part-hovered' : ''}`}
				>
					<Grid columns={4} verticalAlign="middle">
						<Grid.Row>
							<Grid.Column
								className="block-icon-grab"
								width={1}
								verticalAlign="middle"
							>
								<Icon
									className="icon-action-grab"
									iconSet="Lucide"
									color="gray"
									name="GripVertical"
								/>
							</Grid.Column>
							<Grid.Column className="block-icon" width={1} textAlign="center">
								<div
									className={`icon-type ${isHovered ? 'icon-type-hovered' : ''}`}
								>
									<Icon iconSet="Lucide" name={typeBlock} color="white" />
								</div>
							</Grid.Column>
							<Grid.Column floated="left" width={12}>
								<div>
									{codeBlock}
									{typeBlock === IconBlockNotePresentation.CHAMP_LIBRE &&
										!_.isNil(contenuCourt) &&
										contenuCourt !== '' &&
										` | ${contenuCourt}`}
								</div>
							</Grid.Column>
							<Grid.Column
								width={2}
								className="block-actions-part"
								textAlign="right"
							>
								{typeBlock === IconBlockNotePresentation.CHAMP_LIBRE && (
									<Icon
										className="icon-action-setting"
										data-testid="settingBlockNotePresentation"
										link
										iconSet="Lucide"
										name="Settings"
										onClick={handleUpdate}
									/>
								)}
								{!isLastLigne && (
									<Icon
										className="icon-action-delete"
										data-testid="deleteBlockNotePresentation"
										link
										iconSet="Lucide"
										name="CircleX"
										onClick={handleDeletingSelection}
										inverted
									/>
								)}
							</Grid.Column>
						</Grid.Row>
					</Grid>
				</Segment>
			</Grid.Column>
			{isHovered && (
				<Grid.Column width={1} textAlign="center">
					{renderBlockList()}
				</Grid.Column>
			)}
			<Modal open={isDeleting}>
				<Modal.Header>Supprimer le bloc</Modal.Header>
				<Modal.Content>
					<p>Voulez-vous vraiment supprimer ce bloc ?</p>
				</Modal.Content>
				<Modal.Actions>
					<Button
						content="Supprimer"
						className="button-action-confirm"
						onClick={handleDelete}
						negative
					/>
					<Button
						content="Annuler"
						className="button-action-cancel"
						onClick={handleDeletingDeselection}
					/>
				</Modal.Actions>
			</Modal>
		</Grid>
	);
};

export default Block;
